import React, { useCallback, useEffect, useMemo, useState } from "react";
import BreadCrumb from "Common/BreadCrumb";
import { Plus, Pencil, Trash2, Loader2 } from "lucide-react";
import TableContainer from "Common/TableContainer";
import Modal from "Common/Components/Modal";
import DeleteModal from "Common/DeleteModal";
import LoadingCounter from "Common/Components/LoadingCounterProps";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import Swal from "sweetalert2";
import { tableButtonClass } from "Common/Components/orderColumns";
import useCurrentRole from "Common/hooks/useCurrentRole";
import { canManageInitialData, isAdminRole } from "Common/constants/roles";
import { apiErrorMessage } from "Common/constants/apiErrors";
import useServerSorting from "Common/hooks/useServerSorting";

const BASE_URL = "https://portal-api.mahanpanel.com";

/** حداکثر طول و تعداد خط مجاز برای هر توضیح، طبق مستند. */
const MAX_LENGTH = 1000;
const MAX_LINES = 2;

interface ProformaDescription {
  id: number;
  description: string;
  created_by_id?: number | null;
  is_shared?: boolean;
}

const extractList = (data: any): any[] => {
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.items)) return data.items;
  if (Array.isArray(data?.data)) return data.data;
  return [];
};

const authHeaders = () => {
  const token =
    localStorage.getItem("app_at_mahan") || sessionStorage.getItem("app_at_mahan");
  return { Authorization: `Bearer ${token}` };
};

/**
 * پیام خطای سرور. برای 422 آرایه‌ی خطاهای validation به یک متن تبدیل می‌شود.
 */
const serverErrorMessage = (error: any, fallback: string): string =>
  apiErrorMessage(error, fallback);

/**
 * خطوط ویندوزی (CRLF) پیش از هر بررسی یا ارسالی به LF تبدیل می‌شوند، وگرنه شمارش
 * خط و طول با آنچه سرور می‌بیند فرق می‌کند.
 */
const normalizeNewlines = (value: string) => value.replace(/\r\n/g, "\n");

/** پیام خطای اعتبارسنجی، یا null اگر مقدار معتبر باشد. */
const validateDescription = (raw: string): string | null => {
  const value = normalizeNewlines(raw);
  if (!value.trim()) return "متن توضیح نمی‌تواند خالی باشد";
  if (value.length > MAX_LENGTH) return `حداکثر ${MAX_LENGTH} کاراکتر مجاز است`;
  if (value.split("\n").length > MAX_LINES) return "حداکثر دو خط مجاز است";
  return null;
};

const Index = () => {
  const navigate = useNavigate();
  const { roleId, userId } = useCurrentRole();
  const canManage = canManageInitialData(roleId);
  const isAdmin = isAdminRole(roleId);

  const mayManageDescription = (row: ProformaDescription) =>
    canManage && (isAdmin || (
      userId !== null &&
      row.created_by_id !== null &&
      row.created_by_id !== undefined &&
      row.is_shared !== true &&
      row.created_by_id === userId
    ));

  const [descriptions, setDescriptions] = useState<ProformaDescription[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [errorMessage, setErrorMessage] = useState<string>("");

  // مودال افزودن/ویرایش. editing برابر null یعنی در حالت افزودن هستیم.
  const [showForm, setShowForm] = useState<boolean>(false);
  const [editing, setEditing] = useState<ProformaDescription | null>(null);
  const [formValue, setFormValue] = useState<string>("");
  const [formError, setFormError] = useState<string>("");
  const [submitting, setSubmitting] = useState<boolean>(false);
  const sorting = useServerSorting();

  const [deleteTarget, setDeleteTarget] = useState<ProformaDescription | null>(null);
  const [deleting, setDeleting] = useState<boolean>(false);

  const fetchDescriptions = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${BASE_URL}/proforma-descriptions/`, {
        headers: authHeaders(),
        params: sorting.sortOrder === "default"
          ? { sort_by: "id", sort_order: "asc" }
          : sorting.params,
      });
      setDescriptions(extractList(res.data));
      setErrorMessage("");
    } catch (error: any) {
      console.error("Error fetching proforma descriptions:", error);
      if (error.response?.status === 401) {
        navigate("/login");
        return;
      }
      if (error.response?.status === 403) {
        setErrorMessage("شما مجوز انجام این عملیات را ندارید");
        setDescriptions([]);
        return;
      }
      setErrorMessage("مشکلی در دریافت فهرست توضیحات رخ داده است.");
    } finally {
      setLoading(false);
    }
  }, [navigate, sorting.sortBy, sorting.sortOrder]);

  useEffect(() => {
    fetchDescriptions();
  }, [fetchDescriptions]);

  const openCreate = () => {
    setEditing(null);
    setFormValue("");
    setFormError("");
    setShowForm(true);
  };

  const openEdit = (row: ProformaDescription) => {
    setEditing(row);
    setFormValue(row.description || "");
    setFormError("");
    setShowForm(true);
  };

  const closeForm = () => {
    setShowForm(false);
    setEditing(null);
    setFormValue("");
    setFormError("");
  };

  /**
   * جلوی ایجاد خط سوم همان موقع تایپ گرفته می‌شود تا کاربر تا لحظه‌ی ارسال منتظر
   * پیام خطا نماند.
   */
  const handleChange = (raw: string) => {
    const value = normalizeNewlines(raw);
    if (value.split("\n").length > MAX_LINES) {
      setFormError("حداکثر دو خط مجاز است");
      return;
    }
    setFormValue(value);
    setFormError(value.length > MAX_LENGTH ? `حداکثر ${MAX_LENGTH} کاراکتر مجاز است` : "");
  };

  const handleSubmit = async () => {
    if (submitting) return;
    const problem = validateDescription(formValue);
    if (problem) {
      setFormError(problem);
      return;
    }

    const payload = { description: normalizeNewlines(formValue).trim() };
    setSubmitting(true);
    try {
      if (editing) {
        const res = await axios.patch(
          `${BASE_URL}/proforma-descriptions/${editing.id}`,
          payload,
          { headers: authHeaders() }
        );
        const updated: ProformaDescription = res.data;
        setDescriptions((prev) =>
          prev.map((row) =>
            row.id === editing.id ? { ...row, ...(updated || payload) } : row
          )
        );
        Swal.fire("عملیات موفق", "توضیح با موفقیت ویرایش شد", "success");
      } else {
        const res = await axios.post(`${BASE_URL}/proforma-descriptions/`, payload, {
          headers: authHeaders(),
        });
        const created: ProformaDescription = res.data;
        if (created?.id) {
          setDescriptions((prev) => [...prev, created]);
        } else {
          await fetchDescriptions();
        }
        Swal.fire("عملیات موفق", "توضیح با موفقیت ثبت شد", "success");
      }
      closeForm();
    } catch (error: any) {
      console.error("Error saving proforma description:", error);
      const status = error.response?.status;
      if (status === 401) {
        navigate("/login");
        return;
      }
      if (status === 403) {
        setFormError("شما مجوز انجام این عملیات را ندارید");
        return;
      }
      if (status === 404) {
        // رکورد در این فاصله حذف شده؛ فهرست را تازه می‌کنیم تا با سرور هماهنگ شود.
        setFormError("این توضیح قبلاً حذف شده یا دیگر وجود ندارد");
        closeForm();
        await fetchDescriptions();
        return;
      }
      setFormError(serverErrorMessage(error, "خطا در ذخیره توضیح"));
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget || deleting) return;
    setDeleting(true);
    try {
      await axios.delete(`${BASE_URL}/proforma-descriptions/${deleteTarget.id}`, {
        headers: authHeaders(),
      });
      setDescriptions((prev) => prev.filter((row) => row.id !== deleteTarget.id));
      setDeleteTarget(null);
      Swal.fire("عملیات موفق", "توضیح با موفقیت حذف شد", "success");
    } catch (error: any) {
      console.error("Error deleting proforma description:", error);
      const status = error.response?.status;
      if (status === 401) {
        navigate("/login");
        return;
      }
      if (status === 403) {
        Swal.fire("خطا", apiErrorMessage(error, "شما مجوز انجام این عملیات را ندارید"), "error");
        return;
      }
      if (status === 404) {
        setDeleteTarget(null);
        Swal.fire("توجه", "این توضیح قبلاً حذف شده یا دیگر وجود ندارد", "info");
        await fetchDescriptions();
        return;
      }
      Swal.fire("خطا", serverErrorMessage(error, "خطا در حذف توضیح"), "error");
    } finally {
      setDeleting(false);
    }
  };

  const columns = useMemo(
    () => [
      {
        header: "ردیف",
        enableColumnFilter: false,
        exportValue: (row: ProformaDescription) =>
          descriptions.findIndex((d) => d.id === row.id) + 1,
        cell: (cell: any) => cell.row.index + 1,
      },
      {
        header: "توضیحات",
        accessorKey: "description",
        // توضیح می‌تواند دو خط باشد، پس شکست خط حفظ می‌شود.
        cell: (cell: any) => (
          <div className="whitespace-pre-line max-w-[42rem]">{cell.getValue() || "-"}</div>
        ),
      },
      {
        header: "دسترسی",
        accessorKey: "is_shared",
        enableSorting: false,
        exportValue: (row: ProformaDescription) => row.is_shared || row.created_by_id === null || row.created_by_id === undefined ? "عمومی" : "شخصی",
        cell: (cell: any) => {
          const row: ProformaDescription = cell.row.original;
          return row.is_shared || row.created_by_id === null || row.created_by_id === undefined ? "عمومی" : "شخصی";
        },
      },
      {
        header: "عملیات",
        enableColumnFilter: false,
        exportable: false,
        cell: (cell: any) => {
          const row: ProformaDescription = cell.row.original;
          if (!mayManageDescription(row)) return <span className="text-xs text-slate-400">—</span>;
          return (
            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                onClick={() => openEdit(row)}
                className={`${tableButtonClass} border-yellow-500 text-yellow-600 hover:bg-yellow-50 dark:text-yellow-400 dark:hover:bg-yellow-900/20`}
                title="ویرایش"
              >
                <Pencil className="size-4" />
                <span>ویرایش</span>
              </button>
              <button
                type="button"
                onClick={() => setDeleteTarget(row)}
                className={`${tableButtonClass} border-red-500 text-red-600 hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-900/20`}
                title="حذف"
              >
                <Trash2 className="size-4" />
                <span>حذف</span>
              </button>
            </div>
          );
        },
      },
    ],
    [canManage, descriptions, isAdmin, userId]
  );

  const remaining = MAX_LENGTH - normalizeNewlines(formValue).length;

  return (
    <React.Fragment>
      <BreadCrumb pageTitle="ثبت توضیحات پیش‌فاکتور" title="ثبت توضیحات پیش‌فاکتور" />
      {errorMessage && <div className="mb-3 text-center text-red-500">{errorMessage}</div>}

      <div className="card">
        <div className="card-body">
          <div className="flex items-center gap-3 mb-4">
            <h6 className="text-15 grow">
              مجموع توضیحات (<b className="total-Employs">{descriptions.length}</b>)
            </h6>
            {canManage && (
              <div className="shrink-0">
                <button
                  type="button"
                  onClick={openCreate}
                  className="text-white btn bg-custom-500 border-custom-500 hover:text-white hover:bg-custom-600"
                >
                  <span className="align-middle rtl">افزودن</span>{" "}
                  <Plus className="inline-block size-4 rtl" />
                </button>
              </div>
            )}
          </div>

          {loading ? (
            <div className="flex items-center justify-center h-32">
              <div className="w-8 h-8 border-4 rounded-full spinner-border animate-spin inline-block border-custom-500 border-t-transparent"></div>
            </div>
          ) : descriptions.length > 0 ? (
            <TableContainer
              isPagination={true}
              columns={columns}
              data={descriptions}
              serverSorting={{ sortBy: sorting.sortBy, sortOrder: sorting.sortOrder, onChange: sorting.onChange }}
              customPageSize={10}
              customnumberfortable={3}
              divclassName="-mx-5 overflow-x-auto"
              tableclassName="w-full whitespace-nowrap"
              theadclassName="ltr:text-left rtl:text-right bg-[#e2e8f0] text-[#334155] dark:bg-[#334155] dark:text-[#f1f5f9]"
              thclassName="px-3.5 py-2.5 first:pl-5 last:pr-5 font-semibold border-b border-slate-300 dark:border-slate-600"
              tdclassName="px-3.5 py-2.5 first:pl-5 last:pr-5 border-y border-slate-200 dark:border-zink-400/80"
              PaginationClassName="flex flex-col items-center gap-4 px-4 mt-4 md:flex-row"
            />
          ) : (
            <LoadingCounter count={1} text="هیچ توضیحی ثبت نشده است" />
          )}
        </div>
      </div>

      {/* ========== مودال افزودن / ویرایش ========== */}
      <Modal
        show={showForm}
        onHide={closeForm}
        modal-center="true"
        dialogClassName="w-screen md:w-[34rem]"
      >
        <Modal.Header
          className="flex items-center justify-between p-4 border-b dark:border-zink-500"
          closeButtonClass="transition-all duration-200 ease-linear text-blue-400 hover:text-red-500"
        >
          <Modal.Title className="text-16">
            {editing ? "ویرایش توضیح" : "افزودن توضیح"}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="p-4">
          <label htmlFor="description" className="block mb-1 text-base font-medium">
            توضیحات:
          </label>
          <textarea
            id="description"
            rows={3}
            className="w-full form-input"
            value={formValue}
            onChange={(e) => handleChange(e.target.value)}
            onKeyDown={(e) => {
              // Enter اول مجاز است؛ Enter دومی که خط سوم می‌سازد گرفته می‌شود.
              if (
                e.key === "Enter" &&
                normalizeNewlines(formValue).split("\n").length >= MAX_LINES
              ) {
                e.preventDefault();
                setFormError("حداکثر دو خط مجاز است");
              }
            }}
            placeholder="متن توضیح (حداکثر دو خط)"
          />
          <div className="flex items-center justify-between mt-1 text-xs">
            <span className="text-red-500">{formError}</span>
            <span className={remaining < 0 ? "text-red-500" : "text-slate-400"}>
              {remaining} کاراکتر باقی مانده
            </span>
          </div>
        </Modal.Body>
        <Modal.Footer className="flex justify-center p-4 border-t dark:border-zink-500">
          <button
            type="button"
            onClick={handleSubmit}
            disabled={submitting}
            className="btn min-w-[9rem] px-8 py-2.5 mt-2 font-bold text-white bg-custom-500 border-custom-500 hover:bg-custom-600 disabled:opacity-70 disabled:cursor-not-allowed"
          >
            {submitting ? (
              <span className="flex items-center justify-center gap-2">
                <Loader2 className="size-4 animate-spin" />
                در حال ذخیره...
              </span>
            ) : editing ? (
              "ذخیره تغییرات"
            ) : (
              "ثبت توضیح"
            )}
          </button>
        </Modal.Footer>
      </Modal>

      {/* ========== تأیید حذف ========== */}
      {/* deleting جلوی ارسال درخواست تکراری را می‌گیرد. */}
      <DeleteModal
        show={deleteTarget !== null}
        onHide={() => (deleting ? undefined : setDeleteTarget(null))}
        onDelete={handleDelete}
      />
    </React.Fragment>
  );
};

export default Index;
